import React, { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { ShieldCheck, ShieldAlert } from "lucide-react";

// Hash-chain integrity mark: evidence records are sealed into a tamper-evident
// chain one block at a time, then the animation SETTLES into a static verdict —
// "Chain Intact" when every record carries a hash, or "Integrity Issue Detected"
// when a record is missing its seal. Driven by real evidence state. One-shot,
// reduced-motion safe.
const INK = "#20242A", MUTE = "#7b8798", TINT = "#F7F5F1", OK = "#4F7A62", BAD = "#A85353";
const fnv = (s) => { let x = 2166136261; for (const c of String(s)) { x ^= c.charCodeAt(0); x = Math.imul(x, 16777619); } return (x >>> 0).toString(16).padStart(8, "0").slice(0, 8); };

export function HashChainMark({ ok = true, records = [], className }) {
  const labels = (records.length ? records : ["Signed", "e-stamped", "QR sealed", "Scanned", "Verified"]).slice(0, 5);
  const n = labels.length;
  const breakAt = ok ? -1 : n - 1;
  const [sealed, setSealed] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    if (reduce) { setSealed(n); setDone(true); return; }
    let i = 0, timer;
    const tick = () => { i += 1; setSealed(i); if (i < n) timer = setTimeout(tick, 320); else setDone(true); };
    timer = setTimeout(tick, 300);
    return () => clearTimeout(timer);
  }, [n]);

  const bw = 92, gap = 10, x0 = 10;
  return (
    <div className={cn("flex flex-col items-center gap-3", className)} data-testid="hash-chain-mark">
      <svg viewBox="0 0 520 200" className="h-auto w-full max-w-[500px]" role="img" aria-label="Evidence hash chain integrity">
        {labels.map((lab, i) => {
          const x = x0 + i * (bw + gap);
          const isSealed = i < sealed;
          const broken = done && !ok && i >= breakAt;
          const stroke = broken ? BAD : isSealed ? OK : MUTE;
          return (
            <g key={i}>
              {i > 0 && <line x1={x - gap} y1={95} x2={x} y2={95} strokeWidth="1.5" stroke={done && !ok && i >= breakAt ? BAD : i <= sealed ? INK : MUTE} />}
              <rect x={x} y={40} width={bw} height={110} rx="4" fill={TINT} stroke={stroke} strokeWidth={isSealed || broken ? 1.25 : 0.75} />
              <text x={x + 8} y={62} fontFamily="Georgia,serif" fontSize="12" fill={INK}>Record {i + 1}</text>
              <text x={x + 8} y={80} fontFamily="Georgia,serif" fontSize="11" fill={broken ? BAD : INK}>{String(lab).slice(0, 14)}</text>
              <text x={x + 8} y={112} fontFamily="monospace" fontSize="10" fill={MUTE}>{isSealed ? fnv(lab + i) : "hashing…"}</text>
              <text x={x + 8} y={138} fontFamily="Georgia,serif" fontSize="10" fill={broken ? BAD : isSealed ? OK : MUTE}>{broken ? "link broken" : isSealed ? "sealed" : ""}</text>
            </g>
          );
        })}
      </svg>
      {done && (
        <div className="flex items-center gap-1.5 text-sm font-semibold" style={{ color: ok ? OK : BAD }} data-testid="hash-chain-result">
          {ok ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
          {ok ? "Chain Intact" : "Integrity Issue Detected"}
        </div>
      )}
    </div>
  );
}
